import {useEffect, useState} from 'react';
import {
    getZones, createZone, updateZone, deleteZone
} from '@/api/zones.api';

import {Button} from '@/components/ui/button';
import {Badge} from '@/components/ui/badge';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {toast} from 'sonner';
import {Plus, Trash2, Edit} from 'lucide-react';
import GoogleAddressSearch from '@/components/GoogleAddressSearch';
import ZoneCircleMap from '@/components/ZoneCircleMap';

const Zones = () => {
    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentZone, setCurrentZone] = useState(null);

    const initialFormState = {
        zoneName: '',
        center: {lat: null, lng: null, address: ''},
        radius: '',
        baseDistanceKm: 5,
        perKmCharge: '',
        minOrderValue: 0,
        rainFee: 0,
        enableCOD: true,
        status: true,
        // Changed to an array of objects
        taxes: [{taxTitle: '', taxType: 'fix', taxAmount: '', remarks: ""}],
        // Tiered delivery charges. Empty maxOrder = unlimited.
        deliveryChargeSlabs: [{minOrder: 0, maxOrder: '', charge: ''}],
    };

    const [formData, setFormData] = useState(initialFormState);

    useEffect(() => {
        fetchZones();
    }, []);

    const fetchZones = async () => {
        try {
            const res = await getZones();
            setZones(res.data);
        } catch {
            toast.error('Failed to fetch zones');
        } finally {
            setLoading(false);
        }
    };

    // --- Dynamic Tax Row Handlers ---
    const addTaxRow = () => {
        setFormData((prev) => ({
            ...prev,
            taxes: [...prev.taxes, {taxTitle: '', taxType: 'fix', taxAmount: '', remarks: ""}]
        }));
    };

    const removeTaxRow = (index) => {
        const updatedTaxes = formData.taxes.filter((_, i) => i !== index);
        setFormData({...formData, taxes: updatedTaxes});
    };

    const handleTaxChange = (index, field, value) => {
        const updatedTaxes = formData.taxes.map((t, i) =>
            i === index ? {...t, [field]: value} : t
        );
        setFormData({...formData, taxes: updatedTaxes});
    };

    // --- Dynamic Delivery Slab Handlers ---
    const addSlabRow = () => {
        const last = formData.deliveryChargeSlabs[formData.deliveryChargeSlabs.length - 1];
        const nextMin =
            last && last.maxOrder !== '' && Number(last.maxOrder) > 0
                ? Number(last.maxOrder)
                : '';
        setFormData({
            ...formData,
            deliveryChargeSlabs: [
                ...formData.deliveryChargeSlabs,
                {minOrder: nextMin, maxOrder: '', charge: ''}
            ]
        });
    };

    const removeSlabRow = (index) => {
        const updated = formData.deliveryChargeSlabs.filter((_, i) => i !== index);
        setFormData({...formData, deliveryChargeSlabs: updated});
    };

    const handleSlabChange = (index, field, value) => {
        const updated = formData.deliveryChargeSlabs.map((s, i) =>
            i === index ? {...s, [field]: value} : s
        );
        setFormData({...formData, deliveryChargeSlabs: updated});
    };

    // --- Center / Address Handlers ---
    const handlePlaceSelected = ({googleAddress, latitude, longitude}) => {
        setFormData((prev) => ({
            ...prev,
            center: {
                lat: latitude,
                lng: longitude,
                address: googleAddress
            }
        }));
    };

    const handleMapCenterChange = (lat, lng) => {
        setFormData((prev) => ({
            ...prev,
            center: {
                ...prev.center,
                lat,
                lng
            }
        }));
    };

    // --- Dialog Handlers ---
    const openCreateDialog = () => {
        setEditMode(false);
        setCurrentZone(null);
        setFormData(initialFormState);
        setShowDialog(true);
    };

    const openEditDialog = (zone) => {
        setEditMode(true);
        setCurrentZone(zone);
        setFormData({
            zoneName: zone.zoneName,
            center: {
                lat: zone.center?.lat ?? null,
                lng: zone.center?.lng ?? null,
                address: zone.center?.address ?? ''
            },
            radius: zone.radius ?? '',
            baseDistanceKm: zone.baseDistanceKm ?? 5,
            perKmCharge: zone.perKmCharge ?? '',
            minOrderValue: zone.minOrderValue || 0,
            rainFee: zone.rainFee,
            enableCOD: zone.enableCOD,
            status: zone.status,
            taxes: zone.taxes && zone.taxes.length > 0
                ? zone.taxes
                : [{taxTitle: '', taxType: 'fix', taxAmount: '', remarks: ""}],
            deliveryChargeSlabs:
                Array.isArray(zone.deliveryChargeSlabs) && zone.deliveryChargeSlabs.length > 0
                    ? zone.deliveryChargeSlabs.map((s) => ({
                        minOrder: s.minOrder ?? 0,
                        maxOrder: s.maxOrder == null ? '' : s.maxOrder,
                        charge: s.charge ?? '',
                    }))
                    : [{minOrder: 0, maxOrder: '', charge: ''}],
        });
        setShowDialog(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic guard so user gets a useful message instead of a 500.
        if (
            formData.center.lat == null ||
            formData.center.lng == null ||
            !Number.isFinite(Number(formData.center.lat)) ||
            !Number.isFinite(Number(formData.center.lng))
        ) {
            toast.error('Please select the zone center using the address search or map.');
            return;
        }
        if (!formData.radius || Number(formData.radius) <= 0) {
            toast.error('Please enter a radius (km) greater than 0.');
            return;
        }

        const payload = {
            zoneName: formData.zoneName,
            center: {
                lat: Number(formData.center.lat),
                lng: Number(formData.center.lng),
                address: formData.center.address || ''
            },
            radius: Number(formData.radius) || 0,
            baseDistanceKm: Number(formData.baseDistanceKm) || 0,
            perKmCharge: Number(formData.perKmCharge) || 0,
            minOrderValue: Number(formData.minOrderValue) || 0,
            rainFee: Number(formData.rainFee) || 0,
            enableCOD: formData.enableCOD,
            status: formData.status,
            taxes: formData.taxes.map(t => ({
                ...t,
                taxAmount: Number(t.taxAmount)
            })),
            deliveryChargeSlabs: formData.deliveryChargeSlabs
                .filter(
                    (s) =>
                        s.charge !== '' &&
                        s.charge !== null &&
                        s.charge !== undefined
                )
                .map((s) => ({
                    minOrder: Number(s.minOrder) || 0,
                    maxOrder:
                        s.maxOrder === '' || s.maxOrder == null
                            ? null
                            : Number(s.maxOrder),
                    charge: Number(s.charge) || 0,
                })),
        };

        try {
            if (editMode && currentZone) {
                await updateZone(currentZone.id, payload);
                toast.success('Zone updated successfully');
            } else {
                await createZone(payload);
                toast.success('Zone created successfully');
            }
            setShowDialog(false);
            fetchZones();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save zone');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this zone?')) return;
        try {
            await deleteZone(id);
            toast.success('Zone deleted');
            fetchZones();
        } catch {
            toast.error('Failed to delete zone');
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin h-12 w-12 border-2 border-primary border-t-transparent rounded-full"/>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
                <h1 className="text-3xl font-bold">Zones</h1>
                <Button onClick={openCreateDialog} data-testid="zone-add-button">
                    <Plus className="mr-2" size={18}/>
                    Add Zone
                </Button>
            </div>

            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent
                    className="max-w-3xl max-h-[90vh] overflow-y-auto"
                    onPointerDownOutside={(e) => {
                        // Keep dialog open when clicking Google Places autocomplete suggestions
                        const target = e.target;
                        if (target && target.closest && target.closest('.pac-container')) {
                            e.preventDefault();
                        }
                    }}
                    onInteractOutside={(e) => {
                        const target = e.target;
                        if (target && target.closest && target.closest('.pac-container')) {
                            e.preventDefault();
                        }
                    }}
                >
                    <DialogHeader>
                        <DialogTitle>{editMode ? 'Edit Zone' : 'Create Zone'}</DialogTitle>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Basic Info */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <Label>Zone Name *</Label>
                                <Input
                                    value={formData.zoneName}
                                    onChange={(e) => setFormData({...formData, zoneName: e.target.value})}
                                    required
                                    data-testid="zone-name-input"
                                />
                            </div>
                        </div>

                        {/* Center + Radius (Google Maps) */}
                        <div className="space-y-3 border-t pt-4">
                            <div>
                                <Label className="text-lg font-semibold">Zone Area (Circle)</Label>
                                <p className="text-xs text-gray-500">
                                    Search an address (or click on the map) to set the center, then set
                                    the radius in kilometres.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="col-span-2">
                                    <GoogleAddressSearch
                                        label="Search Address (Center Point)"
                                        value={formData.center.address}
                                        onChangeValue={(v) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                center: {...prev.center, address: v}
                                            }))
                                        }
                                        onSelectPlace={handlePlaceSelected}
                                    />
                                </div>
                                <div>
                                    <Label>Radius (km) *</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.1"
                                        placeholder="e.g., 10"
                                        value={formData.radius}
                                        onChange={(e) => setFormData({...formData, radius: e.target.value})}
                                        required
                                        data-testid="zone-radius-input"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs">Center Latitude</Label>
                                    <Input
                                        type="number"
                                        step="any"
                                        value={formData.center.lat ?? ''}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                center: {
                                                    ...prev.center,
                                                    lat: e.target.value === '' ? null : Number(e.target.value)
                                                }
                                            }))
                                        }
                                        data-testid="zone-center-lat"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Center Longitude</Label>
                                    <Input
                                        type="number"
                                        step="any"
                                        value={formData.center.lng ?? ''}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                center: {
                                                    ...prev.center,
                                                    lng: e.target.value === '' ? null : Number(e.target.value)
                                                }
                                            }))
                                        }
                                        data-testid="zone-center-lng"
                                    />
                                </div>
                            </div>

                            <ZoneCircleMap
                                center={formData.center}
                                radiusKm={Number(formData.radius) || 0}
                                onCenterChange={handleMapCenterChange}
                            />
                        </div>

                        {/* Per-distance pricing */}
                        <div className="space-y-3 border-t pt-4">
                            <div>
                                <Label className="text-lg font-semibold">Distance-based Delivery Fee</Label>
                                <p className="text-xs text-gray-500">
                                    The slab charge below covers distance up to “Working up to”. For every km
                                    beyond that, the per-km charge is added.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div>
                                    <Label>Working up to (km)</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.1"
                                        placeholder="e.g., 5"
                                        value={formData.baseDistanceKm}
                                        onChange={(e) =>
                                            setFormData({...formData, baseDistanceKm: e.target.value})
                                        }
                                        data-testid="zone-base-distance-input"
                                    />
                                </div>
                                <div>
                                    <Label>Per KM Charge (₹) — beyond above</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        placeholder="e.g., 10"
                                        value={formData.perKmCharge}
                                        onChange={(e) =>
                                            setFormData({...formData, perKmCharge: e.target.value})
                                        }
                                        data-testid="zone-per-km-charge-input"
                                    />
                                </div>
                                <div>
                                    <Label>Min Order Value</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        placeholder="e.g., 199"
                                        value={formData.minOrderValue}
                                        onChange={(e) =>
                                            setFormData({...formData, minOrderValue: Number(e.target.value)})
                                        }
                                        data-testid="zone-min-order-value"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label>Rain Fee</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    value={formData.rainFee}
                                    onChange={(e) => setFormData({...formData, rainFee: Number(e.target.value)})}
                                    data-testid="zone-rain-fee-input"
                                />
                            </div>
                        </div>

                        {/* DYNAMIC DELIVERY CHARGE SLABS (by order subtotal) */}
                        <div className="space-y-3 border-t pt-4">
                            <div className="flex justify-between items-center">
                                <div>
                                    <Label className="text-lg font-semibold">Delivery Charge Slabs</Label>
                                    <p className="text-xs text-gray-500">
                                        Tiered fees by order subtotal. Leave Max Order empty for &quot;unlimited&quot;.
                                    </p>
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={addSlabRow}
                                    data-testid="zone-add-slab-button"
                                >
                                    <Plus className="mr-1" size={14}/> Add Slab
                                </Button>
                            </div>

                            {formData.deliveryChargeSlabs.map((slab, index) => (
                                <div
                                    key={index}
                                    className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end bg-orange-50/50 border border-orange-100 p-3 rounded-lg"
                                    data-testid={`zone-slab-row-${index}`}
                                >
                                    <div className="col-span-4">
                                        <Label className="text-xs">Min Order (≥)</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            placeholder="0"
                                            value={slab.minOrder}
                                            onChange={(e) => handleSlabChange(index, 'minOrder', e.target.value)}
                                            data-testid={`zone-slab-min-${index}`}
                                        />
                                    </div>
                                    <div className="col-span-4">
                                        <Label className="text-xs">
                                            Max Order (&lt;) <span className="text-gray-400">— empty = unlimited</span>
                                        </Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            placeholder="unlimited"
                                            value={slab.maxOrder}
                                            onChange={(e) => handleSlabChange(index, 'maxOrder', e.target.value)}
                                            data-testid={`zone-slab-max-${index}`}
                                        />
                                    </div>
                                    <div className="col-span-3">
                                        <Label className="text-xs">Delivery Charge (₹)</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            placeholder="0 = free"
                                            value={slab.charge}
                                            onChange={(e) => handleSlabChange(index, 'charge', e.target.value)}
                                            required
                                            data-testid={`zone-slab-charge-${index}`}
                                        />
                                    </div>
                                    <div className="col-span-1">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="text-red-500 hover:bg-red-50"
                                            disabled={formData.deliveryChargeSlabs.length === 1}
                                            onClick={() => removeSlabRow(index)}
                                            data-testid={`zone-slab-remove-${index}`}
                                        >
                                            <Trash2 size={16}/>
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* DYNAMIC TAX SECTION */}
                        <div className="space-y-4 border-t pt-4">
                            <div className="flex justify-between items-center">
                                <Label className="text-lg font-semibold">Taxes & Additional Charges</Label>
                                <Button type="button" variant="outline" size="sm" onClick={addTaxRow}>
                                    <Plus className="mr-1" size={14}/> Add Tax
                                </Button>
                            </div>

                            {formData.taxes.map((tax, index) => (
                                <div key={index}
                                     className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end bg-gray-50 p-3 rounded-lg relative">
                                    <div className="col-span-3">
                                        <Label className="text-xs">Tax Title</Label>
                                        <Input
                                            placeholder="e.g. GST"
                                            value={tax.taxTitle}
                                            onChange={(e) => handleTaxChange(index, 'taxTitle', e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="col-span-3">
                                        <Label className="text-xs">Type</Label>
                                        <Select
                                            value={tax.taxType}
                                            onValueChange={(v) => handleTaxChange(index, 'taxType', v)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue/>
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="fix">Fix (₹)</SelectItem>
                                                <SelectItem value="percentage">Percentage (%)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="col-span-3">
                                        <Label className="text-xs">Amount</Label>
                                        <Input
                                            type="number"
                                            value={tax.taxAmount}
                                            onChange={(e) => handleTaxChange(index, 'taxAmount', e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <Label className="text-xs">Remarks</Label>
                                        <Input
                                            type="text"
                                            value={tax.remarks}
                                            onChange={(e) => handleTaxChange(index, 'remarks', e.target.value)}
                                        />
                                    </div>
                                    <div className="col-span-1">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="text-red-500 hover:bg-red-50"
                                            disabled={formData.taxes.length === 1}
                                            onClick={() => removeTaxRow(index)}
                                        >
                                            <Trash2 size={16}/>
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Settings */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t pt-4">
                            <div>
                                <Label>Enable COD</Label>
                                <Select
                                    value={formData.enableCOD.toString()}
                                    onValueChange={(v) => setFormData({...formData, enableCOD: v === 'true'})}
                                >
                                    <SelectTrigger><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="true">Yes</SelectItem>
                                        <SelectItem value="false">No</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Status</Label>
                                <Select
                                    value={formData.status.toString()}
                                    onValueChange={(v) => setFormData({...formData, status: v === 'true'})}
                                >
                                    <SelectTrigger><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="true">Active</SelectItem>
                                        <SelectItem value="false">Inactive</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <Button type="submit" className="w-full" data-testid="zone-submit-button">
                            {editMode ? 'Update Zone' : 'Create Zone'}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>

            {/* TABLE */}
            <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                    <thead className="bg-gray-100">
                    <tr>
                        <th className="p-3 text-left">Zone</th>
                        <th className="p-3 text-left">Area</th>
                        <th className="p-3 text-left">Distance Pricing</th>
                        <th className="p-3 text-left">Taxes/Charges</th>
                        <th className="p-3 text-center">COD</th>
                        <th className="p-3 text-center">Status</th>
                        <th className="p-3 text-right">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {zones.map((z) => (
                        <tr key={z.id} className="border-t hover:bg-gray-50">
                            <td className="p-3 font-medium">
                                {z.zoneName}
                                <div className="text-xs text-gray-500 font-normal">
                                    {Array.isArray(z.deliveryChargeSlabs) && z.deliveryChargeSlabs.length > 0 && (
                                        <div className="mt-1 flex flex-col gap-0.5">
                                            {z.deliveryChargeSlabs.map((s, i) => {
                                                const range =
                                                    s.maxOrder == null
                                                        ? `≥ ₹${s.minOrder ?? 0}`
                                                        : `₹${s.minOrder ?? 0} – ₹${s.maxOrder}`;
                                                const fee = Number(s.charge) === 0 ? 'FREE' : `₹${s.charge}`;
                                                return (
                                                    <span
                                                        key={i}
                                                        data-testid={`zone-slab-pill-${z.id}-${i}`}
                                                        className="inline-flex items-center gap-1 text-[11px] text-orange-700"
                                                    >
                                                        <span className="font-mono">{range}</span>
                                                        <span>→</span>
                                                        <span
                                                            className={`font-semibold ${fee === 'FREE' ? 'text-green-700' : ''}`}>
                                                            {fee}
                                                        </span>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {z.minOrderValue > 0 && (
                                        <div className="mt-0.5">Min Order: ₹{z.minOrderValue}</div>
                                    )}
                                </div>
                            </td>
                            <td className="p-3 text-sm">
                                {z.center?.lat != null && z.center?.lng != null ? (
                                    <div className="flex flex-col gap-1">
                                        <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 w-fit">
                                            Radius: {z.radius || 0} km
                                        </Badge>
                                        {z.center.address && (
                                            <span className="text-xs text-gray-500 line-clamp-2 max-w-[220px]">
                                                {z.center.address}
                                            </span>
                                        )}
                                        <span className="text-[10px] text-gray-400 font-mono">
                                            {Number(z.center.lat).toFixed(4)}, {Number(z.center.lng).toFixed(4)}
                                        </span>
                                    </div>
                                ) : (
                                    <span className="text-gray-400">Not set</span>
                                )}
                            </td>
                            <td className="p-3 text-xs">
                                <div className="flex flex-col gap-0.5">
                                    <span>Up to <b>{z.baseDistanceKm || 0} km</b></span>
                                    <span className="text-gray-500">
                                        + ₹{z.perKmCharge || 0}/km beyond
                                    </span>
                                </div>
                            </td>
                            <td className="p-3">
                                <div className="flex flex-wrap gap-1">
                                    {z.taxes?.map((t, idx) => (
                                        <Badge key={idx} variant="secondary" className="text-[10px]">
                                            {t.taxTitle}: {t.taxType === 'percentage' ? `${t.taxAmount}%` : `₹${t.taxAmount}`}
                                        </Badge>
                                    ))}
                                    {(!z.taxes || z.taxes.length === 0) &&
                                        <span className="text-gray-400 text-sm">-</span>}
                                </div>
                            </td>
                            <td className="p-3 text-center">
                                <Badge
                                    className={z.enableCOD ? 'bg-green-100 text-green-700 hover:bg-green-100' : 'bg-red-100 text-red-700 hover:bg-red-100'}>
                                    {z.enableCOD ? 'Yes' : 'No'}
                                </Badge>
                            </td>
                            <td className="p-3 text-center">
                                <Badge
                                    className={z.status ? 'bg-green-100 text-green-700 hover:bg-green-100' : 'bg-red-100 text-red-700 hover:bg-red-100'}>
                                    {z.status ? 'Active' : 'Inactive'}
                                </Badge>
                            </td>
                            <td className="p-3 text-right flex justify-end gap-2">
                                <Button size="sm" variant="outline" onClick={() => openEditDialog(z)}
                                        data-testid={`zone-edit-${z.id}`}>
                                    <Edit size={14}/>
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => handleDelete(z.id)}
                                        data-testid={`zone-delete-${z.id}`}>
                                    <Trash2 size={14}/>
                                </Button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
                </div>
            </div>

            {zones.length === 0 && <p className="text-center text-gray-500 mt-8">No zones found</p>}
        </div>
    );
};

export default Zones;
